import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, MessageSquare } from "lucide-react";
import { useCreateConversation } from "../../backend/queries/chat";
import { Button } from "../../components/ui/button";
import type { ButtonProps } from "../../components/ui/button";

type MessageUserButtonProps = Omit<ButtonProps, "onClick"> & {
  userId: string | null | undefined;
  label?: string;
};

/**
 * Opens or reuses a direct message with one person, then deep-links the staff
 * inbox to it.
 *
 * Creating a direct message is idempotent server-side, so pressing this for
 * somebody you already have a thread with reopens that thread rather than
 * creating a second one.
 */
export function MessageUserButton({
  userId,
  label = "Message",
  variant = "outline",
  disabled,
  ...props
}: MessageUserButtonProps) {
  const navigate = useNavigate();
  const createConversation = useCreateConversation();
  const [error, setError] = useState(false);

  const start = async () => {
    if (!userId) return;
    setError(false);
    try {
      const conversation = await createConversation.mutateAsync({
        type: "dm",
        participant_id: userId,
      });
      navigate(`/messages?conversation=${conversation.id}`);
    } catch {
      setError(true);
    }
  };

  return (
    <Button
      type="button"
      variant={error ? "destructive" : variant}
      onClick={() => void start()}
      disabled={disabled || !userId || createConversation.isPending}
      {...props}
    >
      {createConversation.isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <MessageSquare className="h-4 w-4" aria-hidden="true" />
      )}
      {error ? "Try again" : label}
    </Button>
  );
}
